import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import "../../styles/scss/sendMail.scss";

// 送信対象の選び方。宛先はサーバー（/api/resolve_mail_broadcast_targets/）で決める。
const MODES = [
  { key: "cancellation", label: "休講" },
  { key: "members", label: "在籍会員" },
  { key: "recital2027", label: "発表会2027" },
  { key: "individual", label: "個人指定" },
];

const KIND_LABELS = {
  member: "会員",
  trial: "体験",
  recital: "発表会2027",
  individual: "個人",
  manual: "直接入力",
};

const DAY_LABELS = {
  monday: "月",
  tuesday: "火",
  wednesday: "水",
  thursday: "木",
  friday: "金",
  saturday: "土",
  sunday: "日",
};

// 1 リクエストで送る件数。多すぎるとサーバーのタイムアウトにかかる
const BATCH_SIZE = 10;

const normalizeEmail = (email) => (email || "").trim().toLowerCase();

const lessonLabel = (l, withDay = false) =>
  `${withDay ? `${DAY_LABELS[l.day_of_week] || ""} ` : ""}${l.start_time} ${
    l.name
  }${l.instructor ? `（${l.instructor}）` : ""}`;

async function api(path, { method = "GET", body, query } = {}) {
  const apiUrl = process.env.REACT_APP_API_URL;
  const qs = query ? `?${new URLSearchParams(query)}` : "";
  const res = await fetch(`${apiUrl}/api/${path}/${qs}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `通信に失敗しました（${res.status}）`);
  }
  return data;
}

function SendMailPage() {
  useLoginCheck();

  // ---------- 送信対象 ----------
  const [mode, setMode] = useState("cancellation");

  // 休講
  const [cancelDate, setCancelDate] = useState("");
  const [dayStudios, setDayStudios] = useState([]);
  const [lessonIds, setLessonIds] = useState([]);
  const [includeMembers, setIncludeMembers] = useState(true);
  const [includeTrials, setIncludeTrials] = useState(true);

  // 在籍会員
  const [memberStudios, setMemberStudios] = useState([]);
  const [memberScope, setMemberScope] = useState("all"); // all | studio | lesson
  const [memberStudioId, setMemberStudioId] = useState("");
  const [memberLessonId, setMemberLessonId] = useState("");

  // 個人指定
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [pickedCustomers, setPickedCustomers] = useState([]); // [{customer_id,name,email,active}]
  const [manualEmail, setManualEmail] = useState("");
  const [manualEmails, setManualEmails] = useState([]);

  // ---------- 宛先 ----------
  const [recipients, setRecipients] = useState([]);
  const [targetLabel, setTargetLabel] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState("");
  const [excluded, setExcluded] = useState(() => new Set());
  const [recipientFilter, setRecipientFilter] = useState("");

  // ---------- 本文 ----------
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateModal, setTemplateModal] = useState(null); // null | "pick" | "new"
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ title: "", subject: "", body: "" });

  // ---------- 送信 ----------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [job, setJob] = useState(null); // サーバーの送信状況（_summary）
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [recent, setRecent] = useState([]);
  const [detail, setDetail] = useState(null);

  // ---------- 読み込み ----------
  const loadOptions = useCallback(async () => {
    try {
      const data = await api("get_mail_broadcast_options");
      setMemberStudios(data.studios || []);
      setRecent(data.recent || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await api("get_mail_templates");
      setTemplates(data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadOptions();
    loadTemplates();
  }, [loadOptions, loadTemplates]);

  // 休講: 日付を選んだらその日のレッスンを取る
  useEffect(() => {
    setLessonIds([]);
    if (!cancelDate) {
      setDayStudios([]);
      return;
    }
    let alive = true;
    api("get_mail_broadcast_lessons", { query: { date: cancelDate } })
      .then((data) => alive && setDayStudios(data.studios || []))
      .catch((e) => alive && setResolveError(e.message));
    return () => {
      alive = false;
    };
  }, [cancelDate]);

  // 個人指定: 会員検索（入力が止まってから）
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2 && !/^\d+$/.test(q)) {
      setSearchResults([]);
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      api("search_mail_broadcast_customers", { query: { q } })
        .then((data) => alive && setSearchResults(data.customers || []))
        .catch((e) => console.error(e));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // ---------- 送信対象 → 宛先 ----------
  const target = useMemo(() => {
    switch (mode) {
      case "cancellation":
        if (!cancelDate || lessonIds.length === 0) return null;
        if (!includeMembers && !includeTrials) return null;
        return {
          type: "cancellation",
          date: cancelDate,
          lesson_ids: lessonIds,
          include_members: includeMembers,
          include_trials: includeTrials,
        };
      case "members":
        if (memberScope === "studio") {
          return memberStudioId ? { type: "members", studio_id: Number(memberStudioId) } : null;
        }
        if (memberScope === "lesson") {
          return memberLessonId ? { type: "members", lesson_id: Number(memberLessonId) } : null;
        }
        return { type: "members" };
      case "recital2027":
        return { type: "recital2027" };
      case "individual":
        if (pickedCustomers.length === 0 && manualEmails.length === 0) return null;
        return {
          type: "individual",
          customer_ids: pickedCustomers.map((c) => c.customer_id),
          emails: manualEmails,
        };
      default:
        return null;
    }
  }, [
    mode,
    cancelDate,
    lessonIds,
    includeMembers,
    includeTrials,
    memberScope,
    memberStudioId,
    memberLessonId,
    pickedCustomers,
    manualEmails,
  ]);

  const targetKey = target ? JSON.stringify(target) : "";
  const resolveSeq = useRef(0);

  useEffect(() => {
    setExcluded(new Set());
    setResolveError("");
    if (!targetKey) {
      resolveSeq.current += 1; // 読み込み中の結果は捨てる
      setRecipients([]);
      setTargetLabel("");
      setResolving(false);
      return;
    }
    const seq = ++resolveSeq.current;
    setResolving(true);
    const timer = setTimeout(async () => {
      try {
        const data = await api("resolve_mail_broadcast_targets", {
          method: "POST",
          body: { target: JSON.parse(targetKey) },
        });
        if (seq !== resolveSeq.current) return;
        setRecipients(data.recipients || []);
        setTargetLabel(data.label || "");
      } catch (e) {
        if (seq !== resolveSeq.current) return;
        setRecipients([]);
        setTargetLabel("");
        setResolveError(e.message);
      } finally {
        if (seq === resolveSeq.current) setResolving(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [targetKey]);

  const sendCount = recipients.filter((r) => !excluded.has(normalizeEmail(r.email))).length;

  const visibleRecipients = useMemo(() => {
    const q = recipientFilter.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter(
      (r) => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q)
    );
  }, [recipients, recipientFilter]);

  const toggleExcluded = (email) => {
    const key = normalizeEmail(email);
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setAllVisible = (include) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      visibleRecipients.forEach((r) => {
        const key = normalizeEmail(r.email);
        if (include) next.delete(key);
        else next.add(key);
      });
      return next;
    });
  };

  // ---------- 休講の操作 ----------
  const toggleLesson = (id) =>
    setLessonIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleStudioLessons = (studio) => {
    const ids = studio.lessons.map((l) => l.lesson_id);
    const allOn = ids.every((id) => lessonIds.includes(id));
    setLessonIds((prev) =>
      allOn ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])]
    );
  };

  // ---------- 個人指定の操作 ----------
  const pickCustomer = (c) => {
    setPickedCustomers((prev) =>
      prev.some((p) => p.customer_id === c.customer_id) ? prev : [...prev, c]
    );
  };

  const addManualEmail = () => {
    const email = manualEmail.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      alert("メールアドレスの形式が正しくありません");
      return;
    }
    if (!manualEmails.some((e) => normalizeEmail(e) === normalizeEmail(email))) {
      setManualEmails((prev) => [...prev, email]);
    }
    setManualEmail("");
  };

  // ---------- 定型文 ----------
  const applyTemplate = () => {
    if (!selectedTemplate) return;
    if ((subject || body) && !window.confirm("入力中の件名・本文を定型文で置き換えますか？")) return;
    setSubject(selectedTemplate.subject || "");
    setBody(selectedTemplate.body || "");
    setTemplateModal(null);
  };

  const createTemplate = async () => {
    if (!newTemplate.title.trim() || !newTemplate.subject.trim() || !newTemplate.body.trim()) {
      alert("タイトル・件名・本文を入力してください");
      return;
    }
    try {
      await api("create_mail_template", { method: "POST", body: newTemplate });
      setNewTemplate({ title: "", subject: "", body: "" });
      await loadTemplates();
      setTemplateModal("pick");
    } catch (e) {
      alert(`保存に失敗しました: ${e.message}`);
    }
  };

  const deleteTemplate = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`定型文「${selectedTemplate.title}」を削除しますか？`)) return;
    try {
      await api("delete_mail_template", {
        method: "POST",
        body: { template_id: selectedTemplate.id },
      });
      setSelectedTemplate(null);
      loadTemplates();
    } catch (e) {
      alert(`削除に失敗しました: ${e.message}`);
    }
  };

  // ---------- 送信 ----------
  const canSend = sendCount > 0 && subject.trim() && body.trim() && !resolving;

  // 送信中にページを閉じようとしたら止める（途中まで送った分は履歴から続きを送れる）
  useEffect(() => {
    if (!sending) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [sending]);

  const runBatches = async (jobId) => {
    setSending(true);
    setSendError("");
    try {
      // pending が無くなるまで数件ずつ送る。送信中のまま止まった宛先（sending）は
      // 送り直さない（失敗分の再送で戻す）
      for (;;) {
        const summary = await api("send_mail_broadcast_batch", {
          method: "POST",
          body: { id: jobId, limit: BATCH_SIZE },
        });
        setJob(summary);
        if (summary.pending === 0 || (summary.results || []).length === 0) break;
      }
    } catch (e) {
      setSendError(e.message);
    } finally {
      setSending(false);
      loadOptions();
    }
  };

  const startSend = async () => {
    setSendError("");
    try {
      const created = await api("create_mail_broadcast", {
        method: "POST",
        body: {
          target,
          excluded_emails: [...excluded],
          subject,
          body,
        },
      });
      setConfirmOpen(false);
      setJob(created);
      await runBatches(created.id);
    } catch (e) {
      setSendError(e.message);
    }
  };

  const resumeJob = async (summary) => {
    setJob(summary);
    await runBatches(summary.id);
  };

  const retryJob = async (summary) => {
    try {
      const reset = await api("retry_mail_broadcast", { method: "POST", body: { id: summary.id } });
      setJob(reset);
      await runBatches(summary.id);
    } catch (e) {
      alert(`再送に失敗しました: ${e.message}`);
    }
  };

  const openDetail = async (id) => {
    try {
      setDetail(await api("get_mail_broadcast_detail", { query: { id } }));
    } catch (e) {
      alert(e.message);
    }
  };

  const reuseContent = (d) => {
    if ((subject || body) && !window.confirm("入力中の件名・本文を置き換えますか？")) return;
    setSubject(d.subject || "");
    setBody(d.body || "");
    setDetail(null);
  };

  const closeJob = () => {
    if (sending) return;
    setJob(null);
    setSendError("");
  };

  const memberLessons = useMemo(() => {
    if (memberScope !== "lesson") return [];
    const studio = memberStudios.find((s) => String(s.studio_id) === String(memberStudioId));
    return studio ? studio.lessons : [];
  }, [memberScope, memberStudios, memberStudioId]);

  // ---------- 描画 ----------
  const renderTargetForm = () => {
    switch (mode) {
      case "cancellation":
        return (
          <>
            <p className="note-warn">
              ※ 休講メールを送る前に、HPでレッスンを閉鎖しているか確認してください。
            </p>
            <div className="field">
              <label className="field-label" htmlFor="cancel-date">
                休講日
              </label>
              <input
                id="cancel-date"
                type="date"
                className="text-input date-input"
                value={cancelDate}
                onChange={(e) => setCancelDate(e.target.value)}
              />
            </div>
            {cancelDate && (
              <div className="field">
                <div className="field-label">休講するレッスン</div>
                {dayStudios.length === 0 ? (
                  <p className="empty-note">この日に開講しているレッスンはありません。</p>
                ) : (
                  <div className="studio-groups">
                    {dayStudios.map((s) => {
                      const ids = s.lessons.map((l) => l.lesson_id);
                      const allOn = ids.every((id) => lessonIds.includes(id));
                      return (
                        <div className="studio-group" key={s.studio_id}>
                          <button
                            type="button"
                            className={`chip studio-chip${allOn ? " on" : ""}`}
                            onClick={() => toggleStudioLessons(s)}
                          >
                            {s.studio_name}
                          </button>
                          <div className="chip-row">
                            {s.lessons.map((l) => (
                              <button
                                type="button"
                                key={l.lesson_id}
                                className={`chip${lessonIds.includes(l.lesson_id) ? " on" : ""}`}
                                onClick={() => toggleLesson(l.lesson_id)}
                              >
                                {lessonLabel(l)}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div className="field">
              <div className="field-label">送る相手</div>
              <div className="chip-row">
                <button
                  type="button"
                  className={`chip${includeMembers ? " on" : ""}`}
                  onClick={() => setIncludeMembers((v) => !v)}
                >
                  会員
                </button>
                <button
                  type="button"
                  className={`chip${includeTrials ? " on" : ""}`}
                  onClick={() => setIncludeTrials((v) => !v)}
                >
                  体験（その日に予約している人）
                </button>
              </div>
            </div>
          </>
        );
      case "members":
        return (
          <>
            <p className="hint">現在のプランで在籍している会員に送ります。休会中の人も含みます。</p>
            <div className="field">
              <div className="field-label">範囲</div>
              <div className="chip-row">
                {[
                  ["all", "全員"],
                  ["studio", "店舗で絞る"],
                  ["lesson", "レッスンで絞る"],
                ].map(([key, label]) => (
                  <button
                    type="button"
                    key={key}
                    className={`chip${memberScope === key ? " on" : ""}`}
                    onClick={() => {
                      setMemberScope(key);
                      setMemberLessonId("");
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {memberScope !== "all" && (
              <div className="field-row">
                <div className="field">
                  <label className="field-label" htmlFor="member-studio">
                    店舗
                  </label>
                  <select
                    id="member-studio"
                    className="text-input"
                    value={memberStudioId}
                    onChange={(e) => {
                      setMemberStudioId(e.target.value);
                      setMemberLessonId("");
                    }}
                  >
                    <option value="">選択してください</option>
                    {memberStudios.map((s) => (
                      <option key={s.studio_id} value={s.studio_id}>
                        {s.studio_name}
                      </option>
                    ))}
                  </select>
                </div>
                {memberScope === "lesson" && (
                  <div className="field">
                    <label className="field-label" htmlFor="member-lesson">
                      レッスン
                    </label>
                    <select
                      id="member-lesson"
                      className="text-input"
                      value={memberLessonId}
                      disabled={!memberStudioId}
                      onChange={(e) => setMemberLessonId(e.target.value)}
                    >
                      <option value="">選択してください</option>
                      {memberLessons.map((l) => (
                        <option key={l.lesson_id} value={l.lesson_id}>
                          {lessonLabel(l, true)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </>
        );
      case "recital2027":
        return (
          <p className="hint">
            発表会2027に参加で申し込んだ人全員に送ります。宛先は会員の現在のメールアドレスです（マイページで変更した人にも届きます）。
          </p>
        );
      case "individual":
        return (
          <>
            <div className="field">
              <label className="field-label" htmlFor="customer-search">
                会員を探す（氏名・メール・会員ID）
              </label>
              <input
                id="customer-search"
                type="search"
                className="text-input"
                placeholder="例: 山田 / yamada@ / 1234"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchResults.length > 0 && (
                <ul className="search-results">
                  {searchResults.map((c) => {
                    const picked = pickedCustomers.some((p) => p.customer_id === c.customer_id);
                    return (
                      <li key={c.customer_id}>
                        <span className="sr-name">
                          {c.name || "（氏名なし）"}
                          <span className={`pill ${c.active ? "pill-active" : "pill-left"}`}>
                            {c.active ? "在籍" : "退会"}
                          </span>
                        </span>
                        <span className="sr-mail">{c.email}</span>
                        <button
                          type="button"
                          className="row-btn"
                          disabled={picked}
                          onClick={() => pickCustomer(c)}
                        >
                          {picked ? "追加済み" : "追加"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="field">
              <label className="field-label" htmlFor="manual-email">
                メールアドレスを直接入れる
              </label>
              <div className="inline-form">
                <input
                  id="manual-email"
                  type="email"
                  className="text-input"
                  placeholder="name@example.com"
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addManualEmail();
                    }
                  }}
                />
                <button type="button" className="row-btn" onClick={addManualEmail}>
                  追加
                </button>
              </div>
            </div>
            {(pickedCustomers.length > 0 || manualEmails.length > 0) && (
              <div className="field">
                <div className="field-label">選んだ相手</div>
                <div className="chip-row">
                  {pickedCustomers.map((c) => (
                    <span className="chip on removable" key={`c${c.customer_id}`}>
                      {c.name || c.email}
                      <button
                        type="button"
                        aria-label="外す"
                        onClick={() =>
                          setPickedCustomers((prev) =>
                            prev.filter((p) => p.customer_id !== c.customer_id)
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {manualEmails.map((email) => (
                    <span className="chip on removable" key={`m${email}`}>
                      {email}
                      <button
                        type="button"
                        aria-label="外す"
                        onClick={() => setManualEmails((prev) => prev.filter((e) => e !== email))}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      default:
        return null;
    }
  };

  const jobDone = job && !sending && job.pending === 0;
  const progress = job && job.total ? Math.round(((job.sent + job.failed) / job.total) * 100) : 0;

  return (
    <div className="send-mail-page">
      <div className="page-header">
        <h1>メール送信</h1>
      </div>

      <p className="lead t-body">
        送信対象を選んで宛先を確認してから送ります。メールは<b>1人1通</b>で届き、ほかの人のアドレスは見えません。
        送ると <b>cc@guluu.jp</b> に控えが1通届きます。
      </p>

      <div className="send-grid">
        <div className="col">
          {/* 送信対象 */}
          <section className="panel">
            <div className="panel-head">
              <h2>送信対象</h2>
            </div>
            <div className="panel-body">
              <div className="mode-tabs" role="tablist">
                {MODES.map((m) => (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === m.key}
                    key={m.key}
                    className={`mode-tab${mode === m.key ? " on" : ""}`}
                    onClick={() => setMode(m.key)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {renderTargetForm()}
            </div>
          </section>

          {/* 宛先 */}
          <section className="panel">
            <div className="panel-head">
              <h2>宛先</h2>
              <span className="count">
                {resolving ? (
                  "読み込み中…"
                ) : (
                  <>
                    <b>{sendCount}</b> 件に送信
                    {excluded.size > 0 && <span className="t-muted">（{excluded.size} 件を除外）</span>}
                  </>
                )}
              </span>
            </div>
            <div className="panel-body">
              {resolveError && <p className="note-warn">{resolveError}</p>}
              {!target && !resolveError && (
                <p className="empty-note">送信対象を選ぶと、ここに宛先が表示されます。</p>
              )}
              {target && !resolving && recipients.length === 0 && !resolveError && (
                <p className="empty-note">該当する宛先がありません。</p>
              )}
              {recipients.length > 0 && (
                <>
                  {targetLabel && <p className="target-label">{targetLabel}</p>}
                  <div className="list-tools">
                    <input
                      type="search"
                      className="text-input"
                      placeholder="一覧を絞り込む（氏名・メール）"
                      value={recipientFilter}
                      onChange={(e) => setRecipientFilter(e.target.value)}
                    />
                    <button type="button" className="link-btn" onClick={() => setAllVisible(true)}>
                      すべて送る
                    </button>
                    <button type="button" className="link-btn" onClick={() => setAllVisible(false)}>
                      すべて外す
                    </button>
                  </div>
                  <div className="table-wrap">
                    <table className="recipient-table">
                      <thead>
                        <tr>
                          <th className="c-check">送る</th>
                          <th>氏名</th>
                          <th>メールアドレス</th>
                          <th>区分</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRecipients.map((r) => {
                          const off = excluded.has(normalizeEmail(r.email));
                          return (
                            <tr key={r.email} className={off ? "off" : ""}>
                              <td className="c-check">
                                <input
                                  type="checkbox"
                                  checked={!off}
                                  onChange={() => toggleExcluded(r.email)}
                                  aria-label={`${r.email} に送る`}
                                />
                              </td>
                              <td className="c-name">{r.name || "—"}</td>
                              <td className="c-mail">{r.email}</td>
                              <td className="c-kind">
                                {r.kinds.map((k) => (
                                  <span className={`pill pill-${k}`} key={k}>
                                    {KIND_LABELS[k] || k}
                                  </span>
                                ))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </section>
        </div>

        <div className="col">
          {/* 本文 */}
          <section className="panel">
            <div className="panel-head">
              <h2>メール内容</h2>
              <button
                type="button"
                className="row-btn"
                onClick={() => {
                  setSelectedTemplate(null);
                  setTemplateModal("pick");
                }}
              >
                定型文
              </button>
            </div>
            <div className="panel-body">
              <div className="field">
                <label className="field-label" htmlFor="mail-subject">
                  件名
                </label>
                <input
                  id="mail-subject"
                  className="text-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div className="field">
                <label className="field-label" htmlFor="mail-body">
                  本文
                </label>
                <textarea
                  id="mail-body"
                  className="text-input body-input"
                  rows={18}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </div>
              <div className="send-row">
                <button
                  type="button"
                  className="send-btn"
                  disabled={!canSend || sending}
                  onClick={() => setConfirmOpen(true)}
                >
                  {sendCount > 0 ? `${sendCount} 件に送信` : "送信"}
                </button>
              </div>
            </div>
          </section>

          {/* 最近の送信 */}
          <section className="panel">
            <div className="panel-head">
              <h2>最近の送信</h2>
            </div>
            <div className="panel-body">
              {recent.length === 0 ? (
                <p className="empty-note">まだ送信履歴はありません。</p>
              ) : (
                <ul className="history">
                  {recent.map((h) => (
                    <li key={h.id}>
                      <div className="h-main">
                        <button type="button" className="h-subject" onClick={() => openDetail(h.id)}>
                          {h.subject}
                        </button>
                        <div className="h-meta t-muted">
                          {h.created_at}・{h.target_label}
                          {h.sent_by && `・${h.sent_by}`}
                        </div>
                      </div>
                      <div className="h-side">
                        <span className={`pill ${h.failed || h.remaining ? "pill-warn" : "pill-ok"}`}>
                          {h.sent}/{h.total} 送信
                          {h.failed > 0 && `・失敗 ${h.failed}`}
                        </span>
                        {h.pending > 0 && (
                          <button
                            type="button"
                            className="row-btn"
                            disabled={sending}
                            onClick={() => resumeJob(h)}
                          >
                            続きを送る
                          </button>
                        )}
                        {(h.failed > 0 || (h.sending > 0 && h.pending === 0)) && (
                          <button
                            type="button"
                            className="row-btn"
                            disabled={sending}
                            onClick={() => retryJob(h)}
                          >
                            失敗分を再送
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* 送信の確認 */}
      {confirmOpen && (
        <div className="modal-overlay" onClick={() => setConfirmOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>この内容で送信しますか？</h3>
              <button type="button" className="modal-close" onClick={() => setConfirmOpen(false)}>
                ×
              </button>
            </div>
            <div className="modal-meta">
              <div>
                <span className="k">対象</span>
                <span className="v">{targetLabel}</span>
              </div>
              <div>
                <span className="k">宛先</span>
                <span className="v">
                  <b>{sendCount} 件</b>（1人1通）
                  {excluded.size > 0 && `・${excluded.size} 件を除外`}
                </span>
              </div>
              <div>
                <span className="k">件名</span>
                <span className="v">{subject}</span>
              </div>
            </div>
            <pre className="modal-body mail-preview">{body}</pre>
            {sendError && <p className="note-warn modal-error">{sendError}</p>}
            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={() => setConfirmOpen(false)}>
                キャンセル
              </button>
              <button type="button" className="btn-send" onClick={startSend}>
                {sendCount} 件に送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 送信の進み具合 */}
      {job && !confirmOpen && (
        <div className="modal-overlay" onClick={closeJob}>
          <div className="modal modal-narrow" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>{sending ? "送信しています…" : jobDone ? "送信が終わりました" : "送信を中断しました"}</h3>
              <button type="button" className="modal-close" onClick={closeJob} disabled={sending}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="progress">
                <div className="progress-bar" style={{ width: `${progress}%` }} />
              </div>
              <p className="progress-text">
                {job.sent + job.failed} / {job.total} 件（送信 {job.sent}・失敗 {job.failed}）
              </p>
              {sending && (
                <p className="hint">このページを閉じずにお待ちください。閉じた場合は「最近の送信」から続きを送れます。</p>
              )}
              {sendError && <p className="note-warn">{sendError}</p>}
              {job.failures && job.failures.length > 0 && (
                <div className="failures">
                  <div className="field-label">送れなかった宛先</div>
                  <ul>
                    {job.failures.map((f) => (
                      <li key={f.email}>
                        <span className="c-mail">{f.email}</span>
                        <span className="t-muted">{f.error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="modal-actions">
              {!sending && job.pending > 0 && (
                <button type="button" className="btn-cancel" onClick={() => runBatches(job.id)}>
                  続きを送る
                </button>
              )}
              {!sending && job.failed > 0 && (
                <button type="button" className="btn-cancel" onClick={() => retryJob(job)}>
                  失敗分を再送
                </button>
              )}
              <button type="button" className="btn-send" onClick={closeJob} disabled={sending}>
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 送信履歴の中身 */}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>{detail.subject}</h3>
              <button type="button" className="modal-close" onClick={() => setDetail(null)}>
                ×
              </button>
            </div>
            <div className="modal-meta">
              <div>
                <span className="k">日時</span>
                <span className="v">
                  {detail.created_at}
                  {detail.sent_by && `・${detail.sent_by}`}
                </span>
              </div>
              <div>
                <span className="k">対象</span>
                <span className="v">{detail.target_label}</span>
              </div>
              <div>
                <span className="k">結果</span>
                <span className="v">
                  {detail.sent}/{detail.total} 送信・失敗 {detail.failed}・未送信 {detail.remaining}
                </span>
              </div>
            </div>
            <div className="modal-body">
              <pre className="mail-preview">{detail.body}</pre>
              <div className="field-label detail-label">宛先</div>
              <div className="table-wrap">
                <table className="recipient-table">
                  <tbody>
                    {(detail.recipients || []).map((r) => (
                      <tr key={r.email}>
                        <td className="c-name">{r.name || "—"}</td>
                        <td className="c-mail">{r.email}</td>
                        <td className="c-kind">
                          <span className={`pill pill-status-${r.status}`}>
                            {{ sent: "送信済", failed: "失敗", pending: "未送信", sending: "送信中" }[r.status] ||
                              r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={() => reuseContent(detail)}>
                この件名・本文を使う
              </button>
              <button type="button" className="btn-send" onClick={() => setDetail(null)}>
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 定型文 */}
      {templateModal === "pick" && (
        <div className="modal-overlay" onClick={() => setTemplateModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>定型文</h3>
              <button type="button" className="modal-close" onClick={() => setTemplateModal(null)}>
                ×
              </button>
            </div>
            <div className="modal-body template-body">
              <ul className="template-list">
                {templates.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={selectedTemplate && selectedTemplate.id === t.id ? "on" : ""}
                      onClick={() => setSelectedTemplate(t)}
                    >
                      {t.title}
                    </button>
                  </li>
                ))}
                {templates.length === 0 && <li className="empty-note">定型文はまだありません。</li>}
              </ul>
              <div className="template-detail">
                {selectedTemplate ? (
                  <>
                    <div className="field-label">件名</div>
                    <p>{selectedTemplate.subject}</p>
                    <div className="field-label">本文</div>
                    <pre className="mail-preview">{selectedTemplate.body}</pre>
                  </>
                ) : (
                  <p className="empty-note">左から定型文を選んでください。</p>
                )}
              </div>
            </div>
            <div className="modal-actions">
              {selectedTemplate && (
                <button type="button" className="del-btn" onClick={deleteTemplate}>
                  削除
                </button>
              )}
              <span className="spacer" />
              <button type="button" className="btn-cancel" onClick={() => setTemplateModal("new")}>
                新規作成
              </button>
              <button
                type="button"
                className="btn-send"
                disabled={!selectedTemplate}
                onClick={applyTemplate}
              >
                この定型文を使う
              </button>
            </div>
          </div>
        </div>
      )}

      {templateModal === "new" && (
        <div className="modal-overlay" onClick={() => setTemplateModal("pick")}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>定型文を作る</h3>
              <button type="button" className="modal-close" onClick={() => setTemplateModal("pick")}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="field">
                <label className="field-label" htmlFor="tpl-title">
                  定型文の名前
                </label>
                <input
                  id="tpl-title"
                  className="text-input"
                  value={newTemplate.title}
                  onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })}
                />
              </div>
              <div className="field">
                <label className="field-label" htmlFor="tpl-subject">
                  件名
                </label>
                <input
                  id="tpl-subject"
                  className="text-input"
                  value={newTemplate.subject}
                  onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                />
              </div>
              <div className="field">
                <label className="field-label" htmlFor="tpl-body">
                  本文
                </label>
                <textarea
                  id="tpl-body"
                  className="text-input body-input"
                  rows={14}
                  value={newTemplate.body}
                  onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={() => setTemplateModal("pick")}>
                戻る
              </button>
              <button type="button" className="btn-send" onClick={createTemplate}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SendMailPage;
